import {
  TEMPLATE_LIMITS,
  cleanTemplateValue,
  type MessageTemplate,
  type TemplateCategory,
  type TemplateVariable,
} from '@chatapp-inbox/shared';
import { isApiError } from '../../api';

export const CATEGORY_LABEL: Record<TemplateCategory, string> = {
  utility: 'Utilidade',
  marketing: 'Marketing',
  authentication: 'Autenticação',
  other: 'Outro',
};

/** Identifica o modelo na lista (o mesmo nome pode existir em mais de um idioma). */
export const templateKey = (t: Pick<MessageTemplate, 'name' | 'language'>): string =>
  `${t.language}/${t.name}`;

/** O texto da opção na lista: o nome, a categoria e, só se o nome se repete, o idioma. */
export function optionLabel(t: MessageTemplate, all: readonly MessageTemplate[]): string {
  const repeated = all.filter((o) => o.name === t.name).length > 1;
  const name = repeated ? `${t.name} (${t.language})` : t.name;
  return `${name} · ${CATEGORY_LABEL[t.category]}${t.supported ? '' : ' · indisponível'}`;
}

/** Rótulo do campo de uma variável: onde ela entra e como a Meta a chama (`1`, `nome_cliente`…). */
export function variableLabel(v: TemplateVariable): string {
  const part = v.component === 'header' ? 'Cabeçalho' : 'Mensagem';
  return /^\d+$/.test(v.name) ? `${part}: variável ${v.name}` : `${part}: ${v.name}`;
}

export const variableLimit = (v: TemplateVariable): number =>
  v.component === 'header' ? TEMPLATE_LIMITS.headerValue : TEMPLATE_LIMITS.bodyValue;

/** Erro por variável (`key` → texto) dos valores digitados; vazio quando está tudo certo. */
export function valueProblems(
  template: MessageTemplate,
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const problems: Record<string, string> = {};
  for (const v of template.variables) {
    const value = cleanTemplateValue(values[v.key] ?? '');
    if (value === '') problems[v.key] = 'Preencha este campo.';
    else if (value.length > variableLimit(v)) {
      problems[v.key] = `Use no máximo ${variableLimit(v)} caracteres.`;
    }
  }
  return problems;
}

/** Conferência rápida do telefone (o servidor tem a palavra final: `invalid_phone`). */
export function phoneProblem(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return 'Informe o telefone do cliente.';
  if (digits.length < 10)
    return 'Informe o DDD e o número. Fora do Brasil, comece por + e o código do país.';
  if (digits.length > 15) return 'O telefone tem dígitos demais.';
  return null;
}

/** Códigos do servidor que são problema do TELEFONE digitado (aparecem no campo, não no rodapé). */
export const PHONE_ERROR_CODES: ReadonlySet<string> = new Set(['invalid_phone']);

/** O que dizer à pessoa quando o servidor recusa o envio do modelo (o texto cru nem sempre serve). */
export function templateErrorMessage(error: unknown): string {
  if (!isApiError(error)) return 'Não foi possível enviar o modelo. Tente de novo.';
  switch (error.code) {
    case 'invalid_phone':
      return 'Telefone inválido. Informe o DDD e o número (com o código do país, se não for do Brasil).';
    case 'template_not_found':
      return 'Este modelo não está mais disponível. Atualize a lista e escolha outro.';
    case 'line_disabled':
      return 'Este número está desligado. Escolha outro.';
    case 'not_connected':
      return 'A conexão com o WhatsApp não está pronta. Fale com quem administra o sistema.';
    case 'provider_unavailable':
      return 'Não foi possível falar com o WhatsApp agora. Tente de novo em instantes.';
    case 'not_assigned':
      return 'Você não é mais o responsável por esta conversa. O modelo não foi enviado.';
    case 'not_found':
      return 'Esta conversa ou este número não está mais disponível para você.';
    case 'client_id_reused':
      return 'Este envio entrou em conflito com outro. Tente de novo.';
    case 'conversation_exists':
    case 'template_unsupported':
    case 'template_values_invalid':
    case 'template_with_attachment':
    case 'text_too_long':
      return error.message;
    default:
      return error.status === 0 || error.status >= 500
        ? 'Não foi possível enviar o modelo agora. Tente de novo em instantes.'
        : error.message;
  }
}

/** Mensagem da carga da lista de modelos. */
export function loadErrorMessage(error: unknown): string {
  if (isApiError(error) && error.status >= 400 && error.status < 500)
    return templateErrorMessage(error);
  if (isApiError(error) && error.code === 'provider_unavailable')
    return templateErrorMessage(error);
  return 'Não foi possível carregar os modelos de mensagem. Tente de novo.';
}
